import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { useAppContext } from '../../contexts/AppContext';
import { useAppStore } from '../../store/appStore';
import { apiService, BusinessUser } from '../../services/api';
import { ConfirmationModal } from '../../components';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const ROLE_COLOR: Record<string, string> = {
  owner: colors.primary as string,
  admin: colors.warning as string,
  manager: colors.info as string,
  member: colors.success as string,
  viewer: colors.textLight as string,
};
const ROLE_ICON: Record<string, string> = {
  admin: 'shield',
  manager: 'briefcase',
  member: 'person',
  viewer: 'eye',
};
const STATUS_COLOR: Record<string, string> = {
  active: colors.success as string,
  pending: colors.warning as string,
  suspended: colors.error as string,
  left: colors.textLight as string,
};

/** Full team list reachable from the dashboard's Team card "View all" CTA.
 * Load-all (not paginated) — mirrors UserSetupScreen.tsx's own precedent,
 * since team sizes stay small. Resend/remove actions reuse that screen's
 * exact handler logic and ConfirmationModal usage rather than reinventing
 * a parallel implementation. */
export const DashboardTeamAllScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification } = useAppContext();
  const { currentBusiness } = useAppStore();

  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<BusinessUser | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const loadUsers = async () => {
    if (!currentBusiness?._id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.getBusinessUsers(currentBusiness._id);
      setUsers(res.data ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [currentBusiness?._id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.user?.firstName ?? ''} ${u.user?.lastName ?? ''}`.toLowerCase();
      return name.includes(q) || (u.user?.email ?? '').toLowerCase().includes(q) || u.role.toLowerCase().includes(q);
    });
  }, [users, query]);

  const handleRemove = (user: BusinessUser) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!currentBusiness?._id || !selectedUser) return;
    try {
      const result = await apiService.removeUser(currentBusiness._id, selectedUser.userId);
      if (result.statusCode === 1000) {
        showNotification('success', 'User Removed', 'User has been removed from the business');
        loadUsers();
      } else {
        showNotification('error', 'Remove Failed', result.errors?.[0]?.message || 'Failed to remove user');
      }
    } catch {
      showNotification('error', 'Error', 'Failed to remove user');
    } finally {
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const handleResend = (user: BusinessUser) => {
    setSelectedUser(user);
    setShowResendModal(true);
  };

  const handleConfirmResend = async () => {
    if (!currentBusiness?._id || !selectedUser) return;
    try {
      const result = await apiService.resendInvitation(currentBusiness._id, selectedUser.userId);
      if (result.statusCode === 1000) {
        showNotification('success', 'Invitation Resent', 'Invitation has been resent to the user');
        loadUsers();
      } else {
        showNotification('error', 'Resend Failed', result.errors?.[0]?.message || 'Failed to resend invitation');
      }
    } catch {
      showNotification('error', 'Error', 'Failed to resend invitation');
    } finally {
      setShowResendModal(false);
      setSelectedUser(null);
    }
  };

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('dashboard')} activeOpacity={0.7}>
          <DirectionalIcon name="chevron-back" size={20} color={colors.text.primary as string} />
        </TouchableOpacity>
        <View>
          <Text style={styles.headerTitle}>Team</Text>
          <Text style={styles.headerSubtitle}>{users.length} member{users.length === 1 ? '' : 's'}</Text>
        </View>
        <TouchableOpacity style={styles.settingsButton} onPress={() => navigateTo('user-setup')} activeOpacity={0.7}>
          <Ionicons name="settings-outline" size={18} color={colors.text.primary as string} />
        </TouchableOpacity>
      </View>

      <View style={styles.searchBox}>
        <Ionicons name="search-outline" size={16} color={colors.text.secondary as string} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search name, email, role"
          placeholderTextColor={colors.text.secondary as string}
          value={query}
          onChangeText={setQuery}
        />
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary as string} />
        </View>
      ) : (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {filtered.length === 0 ? (
            <View style={styles.empty}>
              <Ionicons name="people-outline" size={24} color={colors.text.secondary as string} />
              <Text style={styles.emptyText}>{users.length === 0 ? 'No team members yet' : 'No matches'}</Text>
            </View>
          ) : (
            filtered.map((u) => (
              <View key={u._id} style={styles.row}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>
                    {(u.user?.firstName?.charAt(0) ?? '') || (u.user?.email?.charAt(0) ?? '?')}
                    {u.user?.lastName?.charAt(0) ?? ''}
                  </Text>
                </View>
                <View style={styles.rowContent}>
                  <View style={styles.nameLine}>
                    <Text style={styles.name} numberOfLines={1}>
                      {u.user?.firstName || u.user?.lastName
                        ? `${u.user?.firstName ?? ''} ${u.user?.lastName ?? ''}`.trim()
                        : (u.user?.email ?? 'Pending invite')}
                    </Text>
                    {u.role === 'owner' && <Ionicons name="star" size={12} color={colors.primary as string} />}
                  </View>
                  <Text style={styles.email} numberOfLines={1}>{u.user?.email ?? ''}</Text>
                  <View style={styles.badgeRow}>
                    <View style={[styles.badge, { backgroundColor: `${ROLE_COLOR[u.role] ?? colors.textLight}20` }]}>
                      {ROLE_ICON[u.role] && (
                        <Ionicons name={ROLE_ICON[u.role] as any} size={11} color={ROLE_COLOR[u.role] ?? (colors.textLight as string)} />
                      )}
                      <Text style={[styles.badgeText, { color: ROLE_COLOR[u.role] ?? colors.textLight }]}>
                        {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                      </Text>
                    </View>
                    <View style={styles.statusLine}>
                      <View style={[styles.statusDot, { backgroundColor: STATUS_COLOR[u.userStatus] ?? colors.textLight }]} />
                      <Text style={styles.statusText}>{u.userStatus}</Text>
                    </View>
                  </View>
                </View>
                {u.role !== 'owner' && (
                  <View style={styles.actions}>
                    {u.userStatus === 'pending' && (
                      <TouchableOpacity style={styles.actionButton} onPress={() => handleResend(u)}>
                        <Ionicons name="mail-outline" size={16} color={colors.warning as string} />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity style={styles.actionButton} onPress={() => handleRemove(u)}>
                      <Ionicons name="trash-outline" size={16} color={colors.error as string} />
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            ))
          )}
        </ScrollView>
      )}

      <ConfirmationModal
        visible={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedUser(null); }}
        onConfirm={handleConfirmRemove}
        title="Remove User"
        message={selectedUser ? `This will remove ${`${selectedUser.user?.firstName ?? ''} ${selectedUser.user?.lastName ?? ''}`.trim() || selectedUser.user?.email || 'this user'} from your business. This action cannot be undone.` : ''}
        confirmText="Remove User"
        cancelText="Cancel"
        type="warning"
      />
      <ConfirmationModal
        visible={showResendModal}
        onClose={() => { setShowResendModal(false); setSelectedUser(null); }}
        onConfirm={handleConfirmResend}
        title="Resend Invitation"
        message={selectedUser ? `This will send a new invitation email to ${`${selectedUser.user?.firstName ?? ''} ${selectedUser.user?.lastName ?? ''}`.trim() || selectedUser.user?.email || 'this user'}.` : ''}
        confirmText="Resend Invitation"
        cancelText="Cancel"
        type="info"
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.base,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  backButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  settingsButton: {
    marginLeft: 'auto',
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    margin: designTokens.spacing.md,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: designTokens.spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: 38,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: designTokens.spacing.md,
    paddingBottom: designTokens.spacing.lg,
    gap: designTokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.sm,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primaryTransparent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.bold,
    color: colors.brand.primary,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  nameLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  name: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  email: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.xs,
    marginTop: 2,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
  },
  badgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
  },
  statusLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  actions: {
    flexDirection: 'row',
    gap: designTokens.spacing.xs,
  },
  actionButton: {
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    borderWidth: 1,
    borderColor: colors.border,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
});
